import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { residences } from "@/data/residences";

import heroImage from "@/assets/residences-hero.webp";
import cityImage from "@/assets/residences-paris-apartment.webp";
import coastalImage from "@/assets/residences-coastal.webp";
import interiorImage from "@/assets/residences-interior.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const whyReasons = [
  {
    title: "A curated selection",
    text: "Each residence is carefully selected based on location, design, and overall experience — ensuring a consistent standard across all destinations.",
  },
  {
    title: "Seamless arrangements",
    text: "From initial request to arrival, every step is handled with precision, allowing you to focus entirely on your stay.",
  },
  {
    title: "Discretion and privacy",
    text: "Your privacy is always respected. Every booking is handled with care and confidentiality.",
  },
  {
    title: "Access to prime locations",
    text: "We provide access to residences in the most desirable areas, often not publicly available.",
  },
  {
    title: "Personal service",
    text: "Every request is treated individually, ensuring a tailored experience aligned with your preferences.",
  },
];

const destinations = [
  { label: "Ibiza", path: "/ibiza/residences" },
  { label: "Marbella", path: null, whatsapp: true },
  { label: "Saint-Tropez", path: "/saint-tropez/residences" },
  { label: "Dubai", path: "/dubai#services", whatsapp: false },
  { label: "Paris", path: null, whatsapp: true },
];

const Residences = () => {
  const navigate = useNavigate();

  const handleDestinationClick = (dest: typeof destinations[0]) => {
    if (dest.path) {
      navigate(dest.path);
    } else if (dest.whatsapp) {
      window.open("https://wa.me/message/2XEKJ5STR2IZJ1", "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Arzani Residences — Private Villas & Apartments Worldwide"
        description="Hand-selected villas and apartments across our destinations, with seamless arrangements tailored to your stay."
        path="/residences"
      />
      <Navigation />

      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Arzani Residences" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-foreground/10" />
        </div>
        <div className="relative z-10 px-8 md:px-16 lg:px-20 pb-20 md:pb-28 max-w-2xl">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 2, ease }}
          >
            Private Residences · Worldwide
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground leading-[1] tracking-wide uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 2, ease }}
          >
            Arzani Residences
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm leading-[1.9] max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 2, ease }}
          >
            A residence is more than a place to stay — it defines how you experience your destination.
          </motion.p>
        </div>
      </section>

      {/* Opening Text */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">The Experience</span>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9] mb-6">
              Through Arzani Residences, each property is carefully selected to offer privacy, comfort, and refined living in the world's most sought-after locations. From contemporary villas overlooking the Mediterranean to exclusive residences in the heart of international cities, every stay is designed to feel effortless from the moment you arrive.
            </p>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9]">
              Each residence is chosen not only for its design and location, but for the experience it creates — allowing you to settle into your destination with ease, discretion, and complete peace of mind.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Destinations Bar */}
      <section className="pb-16 md:pb-24 bg-background">
        <div className="container-luxury">
          <motion.div
            className="border-y border-foreground/10 py-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze text-[10px] tracking-[0.3em] block text-center mb-6">
              Our Destinations
            </span>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {destinations.map((dest) => (
                <button
                  key={dest.label}
                  onClick={() => handleDestinationClick(dest)}
                  className={`px-6 md:px-8 py-2.5 text-[10px] md:text-xs tracking-[0.2em] uppercase font-label border transition-all duration-500 ${
                    dest.path || dest.whatsapp
                      ? "border-foreground/20 text-foreground hover:bg-foreground hover:text-background cursor-pointer"
                      : "border-foreground/10 text-muted-foreground/40 cursor-default"
                  }`}
                >
                  {dest.label}
                  {!dest.path && !dest.whatsapp && <span className="ml-2 text-[8px] tracking-normal normal-case opacity-60">Coming soon</span>}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Residences Grid */}
      <section className="pb-20 md:pb-32 bg-background">
        <div className="container-luxury">
          <motion.div
            className="text-center mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Our Collection</span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase">
              Featured Residences
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {residences.map((residence, i) => (
              <motion.div
                key={residence.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 1, delay: i * 0.1, ease }}
              >
                <Link to={`/residences/${residence.slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden mb-5">
                    <img
                      src={residence.image}
                      alt={residence.name}
                      className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="font-display-medium text-base md:text-lg text-foreground tracking-wide uppercase mb-1">
                    {residence.name}
                  </h3>
                  <p className="font-accent text-muted-foreground text-sm italic mb-2">
                    {residence.location}
                  </p>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-label text-bronze text-xs">From {residence.pricing.low}/week</span>
                    <span className="font-body text-muted-foreground text-xs">· {residence.bedrooms} bedrooms</span>
                    <span className="font-body text-muted-foreground text-xs">· {residence.guests}</span>
                  </div>
                  <span className="font-label text-foreground/60 text-[10px] border-b border-foreground/20 pb-0.5 group-hover:text-foreground group-hover:border-foreground/50 transition-colors duration-700">
                    View details
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Private Villas Banner */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src={interiorImage} alt="Private villa interior" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-foreground/60" />
        </div>
        <div className="relative z-10 container-luxury max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground mb-8 tracking-wide uppercase">
              Private Villas
            </h2>
            <p className="font-body text-primary-foreground/70 text-sm md:text-base leading-[1.9] mb-4">
              Beyond our current selection, a wider collection of exceptional villas is available upon request.
            </p>
            <p className="font-body text-primary-foreground/70 text-sm md:text-base leading-[1.9] mb-10">
              For those seeking something specific — a secluded retreat, a coastal residence, or a villa designed for gathering — our team will carefully curate options tailored to your preferences.
            </p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-primary-foreground/40 text-primary-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-300"
            >
              Get in touch
            </a>
          </motion.div>
        </div>
      </section>

      <section className="pb-20 md:pb-32 bg-background">
        <div className="container-luxury">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, ease }}
          >
            <div className="md:col-span-7">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={coastalImage} alt="Coastal residence" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
            <div className="md:col-span-5 md:pl-8">
              <span className="font-label text-bronze text-[10px] tracking-[0.3em] block mb-4">Coastal & Mediterranean</span>
              <h2 className="font-display text-2xl md:text-3xl text-foreground tracking-wide uppercase mb-5 leading-[1.1]">
                Where Architecture Meets the Sea
              </h2>
              <p className="font-body text-muted-foreground text-sm leading-[1.9]">
                Villas designed with open spaces, natural materials, and uninterrupted views — where the boundary between interior and landscape dissolves entirely.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Editorial Image — City (reversed) */}
      <section className="pb-20 md:pb-32 bg-background">
        <div className="container-luxury">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, ease }}
          >
            <div className="md:col-span-5 md:pr-8 order-2 md:order-1">
              <span className="font-label text-bronze text-[10px] tracking-[0.3em] block mb-4">Urban & International</span>
              <h2 className="font-display text-2xl md:text-3xl text-foreground tracking-wide uppercase mb-5 leading-[1.1]">
                Refined City Living
              </h2>
              <p className="font-body text-muted-foreground text-sm leading-[1.9]">
                Exclusive residences positioned in the heart of the world's most prestigious cities — offering privacy, elegance, and direct access to cultural life.
              </p>
            </div>
            <div className="md:col-span-7 order-1 md:order-2">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={cityImage} alt="City residence" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Second Text + Interior Image */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
            <motion.div
              className="md:col-span-5"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, ease }}
            >
              <span className="font-label text-bronze block mb-6">Your Stay</span>
              <p className="font-body text-primary-foreground/60 text-sm md:text-base leading-[1.9] mb-6">
                Whether for a short stay or an extended visit, Arzani ensures that every detail surrounding your residence is thoughtfully arranged. From arrival coordination and housekeeping to additional services such as private chefs, chauffeurs, and wellness treatments, your stay is fully supported throughout.
              </p>
              <p className="font-body text-primary-foreground/60 text-sm md:text-base leading-[1.9]">
                Our approach remains personal and discreet, ensuring that each residence feels entirely your own, while every detail is managed seamlessly in the background.
              </p>
            </motion.div>
            <motion.div
              className="md:col-span-7"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, delay: 0.15, ease }}
            >
              <div className="aspect-[3/4] md:aspect-[4/5] overflow-hidden">
                <img src={interiorImage} alt="Residence interior" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Book with Arzani */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-5xl">
          <motion.div
            className="text-center mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Why Book With Arzani</span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase">
              The Difference
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-12 md:gap-x-16 md:gap-y-16">
            {whyReasons.map((reason, i) => (
              <motion.div
                key={reason.title}
                className="w-full md:w-[calc(50%-2rem)] lg:w-[calc(33.333%-2.67rem)]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.1, ease }}
              >
                <h3 className="font-display-medium text-base text-foreground tracking-wide uppercase mb-3">
                  {reason.title}
                </h3>
                <div className="w-8 h-px bg-bronze mb-4" />
                <p className="font-body text-muted-foreground text-sm leading-[1.9]">
                  {reason.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">FAQ</span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase">
              Frequently Asked Questions
            </h2>
          </motion.div>

          {(() => {
            const faqs = [
              { q: "How does booking a residence through Arzani work?", a: "After submitting your request with your preferred destination, dates, and requirements, we present a curated selection of available residences. Once you confirm, we handle all arrangements." },
              { q: "What types of residences are available?", a: "We offer a range of properties including private villas, luxury apartments, and exclusive estates across our destinations." },
              { q: "Can I request a specific property?", a: "Yes. If you have a particular property in mind, we will check availability and arrange it for you, subject to the owner's terms." },
              { q: "Is a deposit required?", a: "Yes. A deposit is required to confirm the reservation. Full details are provided during the booking process." },
              { q: "Are additional services included?", a: "Residences can be complemented with services such as private chefs, housekeeping, chauffeurs, and wellness treatments through Arzani Lifestyle." },
              { q: "What is the minimum stay?", a: "Minimum stay requirements vary depending on the property and destination. Our team will advise you during the booking process." },
              { q: "Can I view the residence before booking?", a: "We provide detailed imagery, floor plans, and descriptions for each property. Virtual tours may be available for select residences." },
              { q: "Is early check-in or late check-out possible?", a: "We always aim to accommodate flexible timing, subject to availability and the property owner's approval." },
              { q: "What happens in case of cancellation?", a: "Cancellation policies vary per property and will be communicated clearly before confirmation." },
              { q: "Can residences be arranged last-minute?", a: "Yes, subject to availability. Our team will always aim to accommodate urgent requests." },
            ];
            const left = faqs.slice(0, 5);
            const right = faqs.slice(5);
            return (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-x-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease }}
              >
                <Accordion type="single" collapsible className="w-full">
                  {left.map((item, i) => (
                    <AccordionItem key={i} value={`faq-left-${i}`} className="border-foreground/10">
                      <AccordionTrigger className="font-display-medium text-sm text-foreground tracking-wide uppercase text-left hover:no-underline py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.8]">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                <Accordion type="single" collapsible className="w-full">
                  {right.map((item, i) => (
                    <AccordionItem key={i} value={`faq-right-${i}`} className="border-foreground/10">
                      <AccordionTrigger className="font-display-medium text-sm text-foreground tracking-wide uppercase text-left hover:no-underline py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.8]">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            );
          })()}

          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2, ease }}
          >
            <p className="font-body text-muted-foreground/60 text-sm mb-6">Still have a question?</p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Get In Touch</span>
            <h2 className="font-display text-3xl md:text-4xl text-primary-foreground tracking-wide uppercase mb-6">
              Request a Residence
            </h2>
            <p className="font-body text-primary-foreground/50 text-sm leading-[1.9] mb-10 max-w-lg mx-auto">
              Share your preferred destination, dates, and any special requirements.
              Our team will respond promptly with a tailored selection.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/message/2XEKJ5STR2IZJ1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-10 py-3 border border-primary-foreground/20 text-primary-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-500"
              >
                WhatsApp
              </a>
              <a
                href="mailto:info@arzanigroupworldwide.com"
                className="inline-block px-10 py-3 border border-primary-foreground/20 text-primary-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-500"
              >
                Email Us
              </a>
            </div>
            <p className="font-body text-primary-foreground/30 text-xs mt-8">
              +31 6 19038233 · info@arzanigroupworldwide.com
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Residences;
